"use client";

import { useState, useRef, useCallback } from "react";
import { FabricObject } from "fabric";
import { ProposalCanvas, useProposalCanvas } from "./ProposalCanvas";
import { TemplateToolbar } from "./TemplateToolbar";
import { ContextualToolbar } from "./ContextualToolbar";
import { PropertiesSidebar } from "./PropertiesSidebar";
import { PageTabs } from "./PageTabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import type { TemplatePage, CanvasData, OutputField, FormField } from "@/types";

interface TemplateEditorProps {
  initialPages?: TemplatePage[];
  ownPagesStartIndex?: number; // Index from which own pages start (pages before are inherited)
  onSave: (pages: TemplatePage[], inheritedPageAdditions?: Record<string, CanvasData> | null) => void;
  isSaving?: boolean;
  outputFields?: OutputField[];
  formFields?: FormField[];
}

const DEFAULT_CANVAS_DATA: CanvasData = {
  version: "5.3.0",
  objects: [],
  background: "#ffffff",
};

export function TemplateEditor({
  initialPages,
  ownPagesStartIndex = 0,
  onSave,
  isSaving = false,
  outputFields = [],
  formFields = [],
}: TemplateEditorProps) {
  // Initialize with one page if no initial pages provided
  const [pages, setPages] = useState<TemplatePage[]>(() => {
    if (initialPages && initialPages.length > 0) {
      return initialPages;
    }
    return [
      {
        index: 0,
        name: "Page 1",
        canvas_data: DEFAULT_CANVAS_DATA,
      },
    ];
  });
  
  const [activePageIndex, setActivePageIndex] = useState(() => {
    // Start on the first own page if there are inherited pages and own pages exist
    if (initialPages && initialPages.length > 0 && ownPagesStartIndex > 0) {
      // If there are own pages, start on the first own page
      if (ownPagesStartIndex < initialPages.length) {
        return initialPages[ownPagesStartIndex].index;
      }
      // Otherwise start on the first page (inherited)
      return initialPages[0].index;
    }
    return 0;
  });
  const [hasSelection, setHasSelection] = useState(false);
  const [selectedElement, setSelectedElement] = useState<FabricObject | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  // Shared clipboard ref persists across page switches for cross-page copy/paste
  const clipboardRef = useRef<FabricObject | null>(null);
  const canvas = useProposalCanvas(canvasRef);

  // Get the active page
  const activePage = pages.find((p) => p.index === activePageIndex) || pages[0];
  
  // Check if current page is inherited (read-only)
  const isCurrentPageInherited = activePageIndex < ownPagesStartIndex;

  // Handle canvas changes
  const handleCanvasChange = useCallback((canvasData: CanvasData) => {
    setPages((prevPages) =>
      prevPages.map((page) => {
        if (page.index !== activePageIndex) return page;
        // For inherited pages, save to child_additions (not canvas_data which is the parent's)
        if (activePageIndex < ownPagesStartIndex) {
          return { ...page, child_additions: canvasData };
        }
        return { ...page, canvas_data: canvasData };
      })
    );
    setHasUnsavedChanges(true);
  }, [activePageIndex, ownPagesStartIndex]);

  // Handle selection changes
  const handleSelectionChange = useCallback((hasSel: boolean, obj: FabricObject | null) => {
    setHasSelection(hasSel);
    setSelectedElement(obj);
  }, []);

  // Page management
  const handlePageSelect = (index: number) => {
    // Save current canvas data before switching
    const currentCanvasData = canvas.getCanvasData();
    if (currentCanvasData) {
      setPages((prevPages) =>
        prevPages.map((page) => {
          if (page.index !== activePageIndex) return page;
          // For inherited pages, save to child_additions
          if (activePageIndex < ownPagesStartIndex) {
            return { ...page, child_additions: currentCanvasData };
          }
          return { ...page, canvas_data: currentCanvasData };
        })
      );
    }
    setActivePageIndex(index);
    // Clear selection when switching pages
    setSelectedElement(null);
    setHasSelection(false);
  };

  const handlePageAdd = () => {
    const newIndex = Math.max(...pages.map((p) => p.index)) + 1;
    const newPage: TemplatePage = {
      index: newIndex,
      name: `Page ${pages.length + 1}`,
      canvas_data: DEFAULT_CANVAS_DATA,
    };
    setPages((prevPages) => [...prevPages, newPage]);
    setActivePageIndex(newIndex);
    setHasUnsavedChanges(true);
  };

  const handlePageRemove = (index: number) => {
    if (pages.length <= 1) return;
    
    setPages((prevPages) => {
      const newPages = prevPages.filter((p) => p.index !== index);
      // If we're removing the active page, switch to another one
      if (activePageIndex === index) {
        const newActiveIndex = newPages[0]?.index ?? 0;
        setActivePageIndex(newActiveIndex);
      }
      return newPages;
    });
    setHasUnsavedChanges(true);
  };

  const handlePageRename = (index: number, name: string) => {
    setPages((prevPages) =>
      prevPages.map((page) =>
        page.index === index ? { ...page, name } : page
      )
    );
    setHasUnsavedChanges(true);
  };

  const handlePagesReorder = (newPages: TemplatePage[]) => {
    setPages(newPages);
    setHasUnsavedChanges(true);
  };

  // Save handler
  const handleSave = () => {
    // Get current canvas data
    const currentCanvasData = canvas.getCanvasData();
    const updatedPages = pages.map((page) => {
      if (page.index !== activePageIndex || !currentCanvasData) return page;
      // For inherited pages, save to child_additions
      if (activePageIndex < ownPagesStartIndex) {
        return { ...page, child_additions: currentCanvasData };
      }
      return { ...page, canvas_data: currentCanvasData };
    });
    
    // Extract child additions for inherited pages
    const inheritedPageAdditions: Record<string, CanvasData> = {};
    for (let i = 0; i < ownPagesStartIndex && i < updatedPages.length; i++) {
      const page = updatedPages[i];
      if (page.child_additions && page.child_additions.objects.length > 0) {
        inheritedPageAdditions[String(i)] = page.child_additions;
      }
    }
    
    // Only save own pages (not inherited ones)
    // Inherited pages are always at the beginning
    const ownPages = updatedPages.slice(ownPagesStartIndex);
    
    // Reindex own pages to ensure sequential indices starting from 0
    const reindexedOwnPages = ownPages.map((page, idx) => ({
      ...page,
      index: idx,
      is_inherited: false, // Ensure we mark them as not inherited
    }));
    
    const additions = Object.keys(inheritedPageAdditions).length > 0 ? inheritedPageAdditions : null;
    onSave(reindexedOwnPages, additions);
    setHasUnsavedChanges(false);
  };

  // Toolbar handlers
  const handleAddText = () => canvas.addText();
  const handleAddShape = (type: "rect" | "circle" | "triangle" | "line") => canvas.addShape(type);
  const handleAddImage = (url: string) => canvas.addImage(url);
  const handleAddTable = (rows: number, cols: number) => canvas.addTable(rows, cols);
  const handleAddPlaceholderZone = (type: "pricing_grid" | "output_value" | "input_value" | "data_table") => {
    canvas.addPlaceholderZone(type);
  };
  const handleDelete = () => {
    canvas.deleteSelected();
    setSelectedElement(null);
    setHasSelection(false);
  };
  const handleSetBackground = (color: string) => canvas.setBackgroundColor(color);

  // Element update handler (from contextual toolbar and sidebar)
  const handleElementUpdate = useCallback((properties: Record<string, unknown>) => {
    canvas.updateElement(properties);
    // Refresh the selected element reference to update UI
    const updated = canvas.getSelectedElement();
    if (updated) {
      setSelectedElement(updated);
    }
  }, [canvas]);

  // Z-ordering handlers
  const handleBringForward = useCallback(() => {
    canvas.bringForward();
  }, [canvas]);

  const handleSendBackward = useCallback(() => {
    canvas.sendBackward();
  }, [canvas]);

  const handleBringToFront = useCallback(() => {
    canvas.bringToFront();
  }, [canvas]);

  const handleSendToBack = useCallback(() => {
    canvas.sendToBack();
  }, [canvas]);

  const handleDuplicate = useCallback(() => {
    canvas.duplicateSelected();
  }, [canvas]);

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Page Tabs */}
      <PageTabs
        pages={pages}
        activePageIndex={activePageIndex}
        onPageSelect={handlePageSelect}
        onPageAdd={handlePageAdd}
        onPageRemove={handlePageRemove}
        onPageRename={handlePageRename}
        onPagesReorder={handlePagesReorder}
        ownPagesStartIndex={ownPagesStartIndex}
      />

      {/* Toolbar */}
      <TemplateToolbar
        onAddText={handleAddText}
        onAddShape={handleAddShape}
        onAddImage={handleAddImage}
        onAddTable={handleAddTable}
        onAddPlaceholderZone={handleAddPlaceholderZone}
        onDelete={handleDelete}
        onSetBackground={handleSetBackground}
        onSave={handleSave}
        hasSelection={hasSelection}
        isSaving={isSaving}
        hasUnsavedChanges={hasUnsavedChanges}
        isInheritedPage={isCurrentPageInherited}
      />

      {/* Main content area with canvas and sidebar */}
      <div className="flex-1 flex overflow-hidden">
        {/* Canvas Area */}
        <div className="flex-1 overflow-hidden">
          <ScrollArea className="h-full">
            <div 
              ref={canvasContainerRef}
              className="relative flex items-center justify-center p-8 min-h-full"
            >
              {/* Contextual Toolbar - shows for selected own elements (inherited objects are non-selectable) */}
              {selectedElement && (
                <ContextualToolbar
                  selectedElement={selectedElement}
                  canvasContainerRef={canvasContainerRef}
                  onUpdate={handleElementUpdate}
                  onBringForward={handleBringForward}
                  onSendBackward={handleSendBackward}
                  onBringToFront={handleBringToFront}
                  onSendToBack={handleSendToBack}
                  onDuplicate={handleDuplicate}
                />
              )}

              <ProposalCanvas
                key={activePageIndex}
                initialData={isCurrentPageInherited ? activePage.child_additions : activePage.canvas_data}
                inheritedData={isCurrentPageInherited ? activePage.canvas_data : undefined}
                width={960}
                height={540}
                onChange={handleCanvasChange}
                onSelectionChange={handleSelectionChange}
                canvasRef={canvasRef}
                clipboardRef={clipboardRef}
              />
            </div>
          </ScrollArea>
        </div>

        {/* Properties Sidebar - shows when elements are selected (inherited objects are non-selectable) */}
        <PropertiesSidebar
          selectedElement={selectedElement}
          onUpdate={handleElementUpdate}
          onBringForward={handleBringForward}
          onSendBackward={handleSendBackward}
          onBringToFront={handleBringToFront}
          onSendToBack={handleSendToBack}
          onDuplicate={handleDuplicate}
          onDelete={handleDelete}
          outputFields={outputFields}
          formFields={formFields}
        />
      </div>
    </div>
  );
}
